import type { CreateInvoiceRequest, Product } from 'payments-api/model';
import {
  useGetProductsSuspense,
  useGetUsersSuspense,
  usePostInvoices,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useToast } from 'components/ToastManager';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';

import lilId from '@fragment/lil-id';
import {
  type CurrencyCode,
  currencyPrecision as currencyPrecisionMap,
} from '@fragment/types/currencyCodes';
import {
  type InvoiceRowItem,
  InvoiceItemTable,
} from '@fragment/ui/src/components/Payments/InvoiceItemTable';
import { LayoutRow } from '@fragment/ui/src/components/Payments/LayoutRow';
import { Toast } from '@fragment/ui/src/components/Toast';

import {
  findProductByCode,
  getPayoutUserConfig,
  getProductSelectionUpdates,
  PLATFORM_PAYOUT_USER,
} from './invoiceItemUtils';
import { InvoiceLayout } from './InvoiceLayout';

type ProductWithId = Product & { id: string };

type Props = {
  workspaceId: string;
};

export const CreateInvoicePageContent = ({
  workspaceId,
}: Props): JSX.Element => {
  useDocumentTitle('Create Invoice');

  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data: productsData } = useGetProductsSuspense();
  const { data: usersData } = useGetUsersSuspense();
  const createInvoiceMutation = usePostInvoices();
  const [invoiceItems, setInvoiceItems] = useState<InvoiceRowItem[]>([]);
  const [, setLocation] = useLocation();

  // Form state managed by react-hook-form
  const [buyerUser, setBuyerParty] = useState('');
  const [invoiceId, setInvoiceId] = useState('');

  const users = usersData.data;
  const rawProducts = productsData.data;

  const canPublish = useMemo(() => {
    if (invoiceId.trim() === '' || buyerUser.trim() === '') {
      return false;
    }
    if (invoiceItems.length === 0) {
      return false;
    }

    const buyerRoles = invoiceItems
      .flatMap((item) => {
        const itemProduct = findProductByCode(item.productCode, rawProducts);
        if (itemProduct) return [itemProduct];
        return [];
      })
      .flatMap((product) => product.paid_by_roles.map((role) => role.name));

    if (!buyerRoles.length) {
      return false;
    }

    // Validate all items have required fields
    return invoiceItems.every(
      (item) => item.productCode.trim() !== '' && item.amount > 0
    );
  }, [invoiceId, buyerUser, invoiceItems, rawProducts]);

  // Add id field to products for SubTable compatibility (uses code as id)
  const products: ProductWithId[] = useMemo(
    () =>
      rawProducts.map((product) => ({
        ...product,
        id: product.code,
      })),
    [rawProducts]
  );

  const handleAddBlankInvoiceItem = useCallback(() => {
    const newItem: InvoiceRowItem = {
      id: lilId(),
      productCode: '',
      payoutUser: '',
      description: '',
      amount: 0,
      currency: 'USD',
      currencyPrecision: 2,
      balance: 0,
    };

    setInvoiceItems((prev) => [...prev, newItem]);
  }, []);

  const handleUpdateInvoiceItem = useCallback(
    (id: string, field: string, value: string | number) => {
      setInvoiceItems((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;

          const updates: Partial<InvoiceRowItem> = { [field]: value };

          // When currency changes, also update currencyPrecision so amounts display correctly
          if (field === 'currency' && typeof value === 'string') {
            updates.currencyPrecision =
              currencyPrecisionMap[value as CurrencyCode];
          }

          // When product is selected, auto-set payout party based on product type
          if (field === 'productCode' && typeof value === 'string') {
            const productUpdates = getProductSelectionUpdates(
              value,
              rawProducts
            );
            if (productUpdates) {
              updates.payoutUser = productUpdates.payoutUser;
            }
          }

          return { ...item, ...updates };
        })
      );
    },
    [rawProducts]
  );

  const removeInvoiceItem = useCallback((tempId: string) => {
    setInvoiceItems((prev) => prev.filter((item) => item.id !== tempId));
  }, []);

  const handleCreateInvoice = useCallback(async () => {
    try {
      // Map invoice items to API format
      const lineItems: CreateInvoiceRequest['lineItems'] = invoiceItems.map(
        (item) => ({
          type: InvoiceLineItemType.payout,
          product_id: item.productCode,
          amount: item.amount.toString(),
          currencyCode: item.currency,
          description: item.description,
          user_id:
            item.payoutUser === PLATFORM_PAYOUT_USER ? '' : item.payoutUser,
          platform: item.payoutUser === PLATFORM_PAYOUT_USER,
        })
      );

      const buyerRoles = invoiceItems
        .flatMap((item) => {
          const itemProduct = findProductByCode(item.productCode, rawProducts);
          if (itemProduct) return [itemProduct];
          return [];
        })
        .flatMap((product) => product.paid_by_roles.map((role) => role.name));

      const buyerUserRole = users.find(
        (user) => buyerUser === user.externalId
      )?.role;

      if (!buyerUserRole || !buyerRoles.includes(buyerUserRole)) {
        throw new Error(
          `Buyer user must have one of the required roles: ${buyerRoles.join(
            ', '
          )}`
        );
      }

      const buyerProduct = rawProducts.find((product) =>
        product.paid_by_roles.map((role) => role.name).includes(buyerUserRole!)
      );

      if (!buyerProduct) {
        throw new Error(
          `No product found for buyer with role: ${buyerUserRole}`
        );
      }

      const lineItemWithBuyerProduct = lineItems.find(
        (item) => item.product_id === buyerProduct!.code
      );

      if (!lineItemWithBuyerProduct) {
        throw new Error(
          `Invoice must include at least one item with a product that can be paid by the buyer's role: ${buyerUserRole}`
        );
      }

      // Add payin line item for buyer
      lineItems.push({
        type: InvoiceLineItemType.payin,
        product_id: buyerProduct.code,
        amount: lineItemWithBuyerProduct.amount,
        currencyCode: lineItemWithBuyerProduct.currencyCode,
        description: lineItemWithBuyerProduct.description,
        user_id: buyerUser,
      });

      if (lineItems.length > 0) {
        await createInvoiceMutation.mutateAsync({
          data: {
            invoiceId,
            lineItems,
          },
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['/invoices'] });

      // Redirect to invoices list page
      setLocation(
        makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create invoice. Please try again.';
      showToast(
        <Toast
          type="error"
          message={`Failed to publish invoice: ${errorMessage}`}
        />
      );
    }
  }, [
    createInvoiceMutation,
    invoiceId,
    buyerUser,
    invoiceItems,
    workspaceId,
    setLocation,
    queryClient,
    showToast,
    rawProducts,
    users,
  ]);

  const handleBackToInvoices = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
    );
  }, [workspaceId, setLocation]);

  const totalAmountPerCurrency = useMemo(
    () =>
      invoiceItems.reduce((acc, item) => {
        acc[item.currency] = (acc[item.currency] || 0) + Number(item.amount);
        return acc;
      }, {} as Record<CurrencyCode, number>),
    [invoiceItems]
  );
  const totalAmount = Object.entries(totalAmountPerCurrency).map(
    ([currency, amount]) => ({
      amount: Number(amount),
      currency: currency as CurrencyCode,
    })
  );

  return (
    <>
      <InvoiceLayout
        onBack={handleBackToInvoices}
        title={invoiceId}
        status="draft"
        primaryAction={{
          title: createInvoiceMutation.isPending ? 'Wait...' : 'Publish',
          'data-testid': 'publish-invoice-button',
          onClick: handleCreateInvoice,
          disabled: !canPublish || createInvoiceMutation.isPending,
          disabledTooltipText: createInvoiceMutation.isPending
            ? undefined
            : 'Please fill in all fields and add at least one item.',
        }}
        totalAmount={totalAmount}
        issued={new Date().toISOString()}
        buyer={buyerUser}
        setBuyer={setBuyerParty}
        setTitle={setInvoiceId}
        parties={users}
      >
        <LayoutRow height="fit" justify="start">
          <InvoiceItemTable
            partialEditItemIds={[]} // no existing items when creating an invoice
            invoiceItems={invoiceItems.map((item) => ({
              id: item.id,
              productCode: item.productCode,
              payoutUser: item.payoutUser,
              description: item.description,
              amount: item.amount,
              currency: item.currency,
              currencyPrecision: item.currencyPrecision,
            }))}
            removeInvoiceItem={removeInvoiceItem}
            onAddInvoiceItem={handleAddBlankInvoiceItem}
            showBalance={false}
            setPrice={(itemId: string, amount: number) =>
              handleUpdateInvoiceItem(itemId, 'amount', amount)
            }
            setCurrency={(itemId: string, currency: CurrencyCode) =>
              handleUpdateInvoiceItem(itemId, 'currency', currency)
            }
            setDescription={(itemId: string, description: string) =>
              handleUpdateInvoiceItem(itemId, 'description', description)
            }
            setPayoutUser={(itemId: string, payoutUser: string) =>
              handleUpdateInvoiceItem(itemId, 'payoutUser', payoutUser)
            }
            setProductCode={(itemId: string, productCode: string) =>
              handleUpdateInvoiceItem(itemId, 'productCode', productCode)
            }
            productOptions={products.map((product) => ({
              value: product.code,
              label: product.code,
            }))}
            getPayoutUserOptions={(item) =>
              getPayoutUserConfig(
                item.productCode || undefined,
                rawProducts,
                users
              ).options
            }
          />
        </LayoutRow>
      </InvoiceLayout>
    </>
  );
};

export const CreateInvoicePage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense
    fallback={
      <PaymentViewLayout title="Create New Invoice">
        <div className="text-main-500 text-medium">Loading...</div>
      </PaymentViewLayout>
    }
  >
    <CreateInvoicePageContent workspaceId={workspaceId} />
  </Suspense>
);
